// Задания к урокам (lesson_assignments) и работы учеников (submissions) на бэкенде.

// Тип вопроса задания.
export type QuestionType = 'single_choice' | 'multiple_choice' | 'free_text' | 'file_upload';

// Статус проверки работы ученика.
export type SubmissionStatus = 'pending' | 'accepted' | 'rejected';

// Вариант ответа для вопросов с выбором (single_choice / multiple_choice).
export interface QuestionOption {
  id: string;
  question_id: string;
  sort_order: number;
  option_text: string;
  is_correct: boolean;
}

// Вопрос задания.
export interface AssignmentQuestion {
  id: string;
  assignment_id: string;
  sort_order: number;
  type: QuestionType;
  question_text: string;
  is_required: boolean;
  options?: QuestionOption[];
}

// Задание, привязанное к уроку.
export interface LessonAssignment {
  id: string;
  lesson_id: string;
  title: string;
  description: string;
  auto_accept: boolean;
  // null = без лимита попыток.
  max_attempts: number | null;
  is_published: boolean;
  // version бампается бэкендом при любой мутации вопросов задания.
  version: number;
  created_at: string;
  updated_at: string;
  questions?: AssignmentQuestion[];
}

// Работа ученика, обогащённая данными для списков (JOIN пользователя/курса/урока на бэкенде).
export interface SubmissionWithDetails {
  id: string;
  assignment_id: string;
  user_id: string;
  attempt_no: number;
  // Версия задания, на которую отвечал ученик (снапшот на момент отправки).
  assignment_version: number;
  status: SubmissionStatus;
  is_latest: boolean;
  submitted_at: string;
  graded_at: string | null;
  grader_id: string | null;
  feedback: string;
  user_name: string;
  user_email: string;
  course_title: string;
  lesson_title: string;
  assignment_title: string;
  lesson_id: string;
  course_id: string;
}

// Снапшот вопроса на момент прохождения — хранится вместе с ответом,
// чтобы правки задания задним числом не искажали историю проверки.
export interface QuestionSnapshot {
  type: QuestionType;
  question_text: string;
  is_required: boolean;
  options: {
    id: string;
    option_text: string;
    // omitempty на бэкенде: отсутствует для не-choice вопросов.
    is_correct?: boolean;
  }[];
}

// Тело ответа ученика на вопрос — набор полей зависит от типа вопроса.
export interface AnswerPayload {
  option_ids?: string[];
  text?: string;
  file_key?: string;
}

// Ответ ученика на один вопрос в составе работы.
export interface ReviewAnswer {
  id: string;
  submission_id: string;
  // null, если вопрос был удалён из задания после отправки ответа.
  question_id: string | null;
  question_snapshot: QuestionSnapshot;
  answer: AnswerPayload;
  // null для вопросов, не проверяемых автоматически (free_text / file_upload).
  is_correct: boolean | null;
  file_url: string;
}

// Полная работа ученика с ответами — для страницы проверки.
export interface SubmissionDetails extends SubmissionWithDetails {
  answers: ReviewAnswer[];
}

// Справочник типов вопросов для селектов в UI.
export const QUESTION_TYPES: { id: QuestionType; name: string }[] = [
  { id: 'single_choice', name: 'Один вариант' },
  { id: 'multiple_choice', name: 'Несколько вариантов' },
  { id: 'free_text', name: 'Свободный ответ' },
  { id: 'file_upload', name: 'Файл' },
];

// Справочник статусов проверки для селектов в UI.
export const SUBMISSION_STATUSES: { id: SubmissionStatus; name: string }[] = [
  { id: 'pending', name: 'На проверке' },
  { id: 'accepted', name: 'Принято' },
  { id: 'rejected', name: 'Отклонено' },
];

export function questionTypeLabel(type: QuestionType): string {
  return QUESTION_TYPES.find((item) => item.id === type)?.name ?? type;
}

export function submissionStatusLabel(status: SubmissionStatus): string {
  return SUBMISSION_STATUSES.find((item) => item.id === status)?.name ?? status;
}
